import { useRef, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/lib/toast";
import { fileToSuccessGraphicDataUrl } from "@/lib/successGraphic";
import {
  ShieldCheck,
  UserPlus,
  UserMinus,
  ChevronLeft,
  RefreshCw,
  KeyRound,
  Copy,
  Sparkles,
  Image as ImageIcon,
  X,
} from "lucide-react";

const LOCATIONS = ["Iowa", "Minnesota", "Colorado"] as const;
type LocationValue = (typeof LOCATIONS)[number];

/**
 * Generate a 14-char password using only browser crypto (no server round-trip).
 * Uses an alphabet that excludes visually-ambiguous characters (0/O, 1/l/I).
 */
function generateTempPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const buf = new Uint8Array(14);
  crypto.getRandomValues(buf);
  let out = "";
  for (let i = 0; i < buf.length; i++) out += alphabet[buf[i] % alphabet.length];
  return out;
}

export default function AuthorizedAccounts() {
  const { user, loading } = useAuth({ redirectOnUnauthenticated: true });
  const [, navigate] = useLocation();

  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState<"user" | "admin">("user");
  const [newLocation, setNewLocation] = useState<LocationValue>("Iowa");
  const [newPassword, setNewPassword] = useState("");

  // Per-row graphic upload in-flight tracking (keyed by googleId).
  const [graphicBusy, setGraphicBusy] = useState<string | null>(null);
  const graphicInputRef = useRef<HTMLInputElement>(null);
  const graphicTargetRef = useRef<string | null>(null);

  const [resetTarget, setResetTarget] = useState<{ googleId: string; email: string } | null>(null);
  const [resetPassword, setResetPassword] = useState("");

  // Redirect non-admins
  if (!loading && user && user.role !== "admin") {
    navigate("/");
    return null;
  }

  const { data: accounts = [], refetch } = trpc.admin.listUsers.useQuery(undefined, {
    enabled: !!user && user.role === "admin",
  });

  const createUser = trpc.admin.createUser.useMutation({
    onSuccess: () => {
      toast.success("Account created. Share the temporary password with the user.");
      setNewEmail("");
      setNewName("");
      setNewPassword("");
      setNewRole("user");
      setNewLocation("Iowa");
      refetch();
    },
    onError: (err) => toast.error(`Create failed: ${err.message}`),
  });

  const updateLocation = trpc.admin.updateUserLocation.useMutation({
    onSuccess: () => { toast.success("Location updated."); refetch(); },
    onError: (err) => toast.error(`Update failed: ${err.message}`),
  });

  const updateGraphic = trpc.admin.updateUserGraphic.useMutation({
    onSuccess: () => { toast.success("Success graphic updated."); refetch(); },
    onError: (err) => toast.error(`Update failed: ${err.message}`),
  });

  async function handleGraphicFile(file: File | undefined) {
    const googleId = graphicTargetRef.current;
    if (!file || !googleId) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setGraphicBusy(googleId);
    try {
      const dataUrl = await fileToSuccessGraphicDataUrl(file);
      await updateGraphic.mutateAsync({ googleId, graphicUrl: dataUrl });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not process image");
    } finally {
      setGraphicBusy(null);
      graphicTargetRef.current = null;
    }
  }

  const removeUser = trpc.admin.removeUser.useMutation({
    onSuccess: () => { toast.success("Account removed."); refetch(); },
    onError: (err) => toast.error(`Remove failed: ${err.message}`),
  });

  const updateRole = trpc.admin.updateUserRole.useMutation({
    onSuccess: () => { toast.success("Role updated."); refetch(); },
    onError: (err) => toast.error(`Update failed: ${err.message}`),
  });

  const resetPasswordMutation = trpc.admin.resetUserPassword.useMutation({
    onSuccess: () => {
      toast.success("Password reset. Share the new temporary password with the user.");
      refetch();
    },
    onError: (err) => toast.error(`Reset failed: ${err.message}`),
  });

  async function copyToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select the password manually");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          <ShieldCheck className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Team Access</h1>
            <p className="text-sm text-muted-foreground">
              Manage who can sign in. Users sign in with email + password; passwords are stored hashed and never shown after creation.
            </p>
          </div>
        </div>

        {/* Add new account */}
        <div className="border border-border rounded-lg p-5 mb-6 bg-card space-y-3">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            Add Account
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                placeholder="user@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-name">Name (optional)</Label>
              <Input
                id="new-name"
                placeholder="Jane Doe"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-password">Temporary Password</Label>
            <div className="flex gap-2">
              <Input
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="font-mono text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setNewPassword(generateTempPassword())}
                className="gap-1.5 shrink-0"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Generate
              </Button>
              {newPassword && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(newPassword)}
                  className="gap-1.5 shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              The user must change this on first sign-in.
            </p>
          </div>

          <div className="flex items-end gap-2">
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={newRole} onValueChange={(v) => setNewRole(v as "user" | "admin")}>
                <SelectTrigger className="w-32 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Select value={newLocation} onValueChange={(v) => setNewLocation(v as LocationValue)}>
                <SelectTrigger className="w-36 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="ml-auto"
              disabled={!newEmail.trim() || newPassword.length < 8 || createUser.isPending}
              onClick={() =>
                createUser.mutate({
                  email: newEmail.trim(),
                  name: newName.trim() || undefined,
                  role: newRole,
                  tempPassword: newPassword,
                  defaultLocation: newLocation,
                })
              }
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Add Account
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            <strong>User</strong> — can upload and manage tags. &nbsp;
            <strong>Admin</strong> — can also access Audit Log and Team Access.
          </p>
        </div>

        {/* Existing accounts */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
            <span className="text-sm font-semibold">
              {accounts.length} account{accounts.length !== 1 ? "s" : ""}
            </span>
            <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-7 gap-1 text-xs">
              <RefreshCw className="w-3 h-3" />
              Refresh
            </Button>
          </div>

          {accounts.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground italic">
              No accounts yet.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {accounts.map((entry) => (
                <div
                  key={entry.googleId}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-muted/20 group transition-colors"
                >
                  {/* Avatar initial */}
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-primary uppercase">
                      {(entry.email ?? entry.name ?? "?").charAt(0)}
                    </span>
                  </div>

                  {/* Email + name */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono truncate">{entry.email}</p>
                    {entry.name && (
                      <p className="text-xs text-muted-foreground truncate">{entry.name}</p>
                    )}
                    {entry.mustChangePassword && (
                      <p className="text-xs text-amber-600">Temporary password — not yet changed</p>
                    )}
                  </div>

                  {/* Success graphic — thumbnail (or placeholder). Click to
                      upload; hover shows a remove button when one is set. */}
                  <div className="relative shrink-0">
                    <button
                      type="button"
                      title={entry.successGraphicUrl ? "Change success graphic" : "Set success graphic"}
                      disabled={graphicBusy === entry.googleId}
                      onClick={() => {
                        graphicTargetRef.current = entry.googleId;
                        graphicInputRef.current?.click();
                      }}
                      className="w-9 h-9 rounded-md border border-border overflow-hidden flex items-center justify-center bg-muted/40 hover:border-primary transition-colors"
                    >
                      {graphicBusy === entry.googleId ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                      ) : entry.successGraphicUrl ? (
                        <img
                          src={entry.successGraphicUrl}
                          alt="Success graphic"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                    {entry.successGraphicUrl && graphicBusy !== entry.googleId && (
                      <button
                        type="button"
                        title="Remove — revert to default graphic"
                        onClick={() =>
                          updateGraphic.mutate({ googleId: entry.googleId, graphicUrl: null })
                        }
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>

                  {/* Location selector */}
                  <Select
                    value={entry.defaultLocation ?? "none"}
                    onValueChange={(v) =>
                      updateLocation.mutate({
                        googleId: entry.googleId,
                        location: v === "none" ? null : (v as LocationValue),
                      })
                    }
                  >
                    <SelectTrigger className="w-28 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No default</SelectItem>
                      {LOCATIONS.map((loc) => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Role selector */}
                  <Select
                    value={entry.role}
                    onValueChange={(v) =>
                      updateRole.mutate({ googleId: entry.googleId, role: v as "user" | "admin" })
                    }
                  >
                    <SelectTrigger className="w-24 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Reset password */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 gap-1 text-xs"
                    onClick={() => {
                      setResetTarget({ googleId: entry.googleId, email: entry.email ?? "" });
                      setResetPassword(generateTempPassword());
                    }}
                    title="Reset password"
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                    Reset
                  </Button>

                  {/* Remove */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => {
                      if (
                        confirm(
                          `Remove ${entry.email}? They will be signed out immediately and lose access.`,
                        )
                      ) {
                        removeUser.mutate({ googleId: entry.googleId });
                      }
                    }}
                    title="Remove account"
                  >
                    <UserMinus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Shared hidden file input for per-row success-graphic uploads.
          graphicTargetRef holds which account the chosen file applies to. */}
      <input
        ref={graphicInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          void handleGraphicFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      {/* Reset password dialog */}
      <Dialog
        open={!!resetTarget}
        onOpenChange={(open) => {
          if (!open) {
            setResetTarget(null);
            setResetPassword("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new temporary password for{" "}
              <span className="font-mono">{resetTarget?.email}</span>. They will be forced to
              change it on their next sign-in.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="reset-password">Temporary Password</Label>
            <div className="flex gap-2">
              <Input
                id="reset-password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="font-mono text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setResetPassword(generateTempPassword())}
                className="gap-1.5 shrink-0"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Generate
              </Button>
              {resetPassword && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(resetPassword)}
                  className="gap-1.5 shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </Button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setResetTarget(null);
                setResetPassword("");
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!resetTarget || resetPassword.length < 8 || resetPasswordMutation.isPending}
              onClick={() => {
                if (!resetTarget) return;
                resetPasswordMutation.mutate(
                  { googleId: resetTarget.googleId, tempPassword: resetPassword },
                  {
                    onSuccess: () => {
                      setResetTarget(null);
                      setResetPassword("");
                    },
                  },
                );
              }}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
